import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  UsersService,
  CategoriesService,
  CompetitionsService,
  AdminService,
  AdminUserResponse,
  PermissionDetails,
  CategoryItem,
  CompetitionItem,
  CategorySyncItem,
  CompetitionSyncItem,
  UserCreateRequest,
  UserCreateResponse,
  CountryItem,
  PriorityVideoItem,
  ManualVideoPriorityUpdateResponse,
  OrchestratorAdminJobItem,
  OrchestratorAdminJobsResponse,
  OrchestratorAdminJobUpdateRequest,
  CountriesService,
  CompetitionPriorityUpdateResponse,
  InternalAdminVideoItem,
  InternalAdminVideosResponse,
} from '@solverml/api';

export type {
  CountryItem,
  PriorityVideoItem,
  ManualVideoPriorityUpdateResponse,
  OrchestratorAdminJobItem,
  OrchestratorAdminJobsResponse,
  OrchestratorAdminJobUpdateRequest,
  CompetitionPriorityUpdateResponse,
  InternalAdminVideoItem,
  InternalAdminVideosResponse,
} from '@solverml/api';

export interface CompetitionPriorityItem extends CompetitionItem {
  is_priority?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AdminDataService {
  constructor(
    private usersService: UsersService,
    private categoriesService: CategoriesService,
    private competitionsService: CompetitionsService,
    private adminService: AdminService,
    private countriesService: CountriesService,
  ) { }

  // --- MÉTODOS DE USUARIOS ---

  public getUsers(): Observable<AdminUserResponse[]> {
    return this.usersService.getAllUsersForAdmin();
  }

  public updateUserPermissions(userId: string, permissions: PermissionDetails): Observable<any> {
    return this.usersService.setUserPermissions(userId, permissions);
  }

  public createUser(userData: UserCreateRequest): Observable<UserCreateResponse> {
    return this.usersService.createNewUser(userData);
  }

  public deleteUser(user_id: string): Observable<any> {
    return this.usersService.deleteUser(user_id);
  }

  public getCountries(): Observable<CountryItem[]> {
    return this.countriesService.getAllCountries()
  }

  // --- MÉTODOS DE CATEGORÍAS ---

  public getCategories(): Observable<CategoryItem[]> {
    return this.categoriesService.getAllCategories();
  }

  public syncCategories(categories: CategorySyncItem[]): Observable<CategoryItem[]> {
    return this.categoriesService.syncCategories(categories);
  }

  // --- MÉTODOS DE COMPETICIONES ---

  public getCompetitions(): Observable<CompetitionItem[]> {
    return this.competitionsService.getAllCompetitions();
  }

  public syncCompetitions(competitions: CompetitionSyncItem[]): Observable<CompetitionItem[]> {
    return this.competitionsService.syncCompetitions(competitions);
  }

  public getCompetitionsWithPriority(): Observable<CompetitionPriorityItem[]> {
    return this.competitionsService.getAllCompetitions() as Observable<CompetitionPriorityItem[]>;
  }

  public updateCompetitionPriorities(competitionIds: number[]): Observable<CompetitionPriorityUpdateResponse> {
    return this.competitionsService.updateCompetitionsPriorities({ competition_ids: competitionIds });
  }

  public searchVideosForPriority(searchQuery: string, limit: number = 30): Observable<PriorityVideoItem[]> {
    return this.adminService.searchPriorityVideos(searchQuery, limit);
  }

  public updateManualVideoPriority(
    videoIds: string[],
    prioritize: boolean
  ): Observable<ManualVideoPriorityUpdateResponse> {
    return this.adminService.updatePriorityVideos({ video_ids: videoIds, prioritize });
  }

  // --- Metodo de eliminación en cascada de un video ---

  public deleteVideo(videoId: string): Observable<string> {
    return this.adminService.deleteVideoFullEndpoint(videoId);
  }

  public getOrchestratorJobs(
    state: string | null = null,
    searchQuery: string = '',
    limit: number = 250
  ): Observable<OrchestratorAdminJobsResponse> {
    return this.adminService.listOrchestratorJobs(state ?? undefined, searchQuery, limit);
  }

  public updateOrchestratorJob(
    videoId: string,
    payload: OrchestratorAdminJobUpdateRequest
  ): Observable<OrchestratorAdminJobItem> {
    return this.adminService.updateOrchestratorJob(videoId, payload);
  }

  public getInternalAdminVideos(
    page: number,
    pageSize: number = 10,
    searchQuery: string = ''
  ): Observable<InternalAdminVideosResponse> {
    return this.adminService.listInternalAdminVideos(page, pageSize, searchQuery);
  }

}
